import React, { useEffect, useState } from "react";
import { TrendingUp, RefreshCw } from "lucide-react";

const CACHE_KEY = "lln_indicators_v1";
const TTL_MS = 60 * 60 * 1000; // 1h

async function fetchIndicators() {
  // mindicador.cl is a free, public API (no key) - returns UF and dolar for today
  const res = await fetch("https://mindicador.cl/api");
  if (!res.ok) throw new Error("indicators fetch failed");
  const data = await res.json();
  return {
    uf: data?.uf?.valor ?? null,
    dolar: data?.dolar?.valor ?? null,
    fetched_at: Date.now(),
    fecha: data?.uf?.fecha || data?.dolar?.fecha || null,
  };
}

function formatCLP(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n);
}

export default function ChileIndicators({ variant = "light" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (force = false) => {
    setLoading(true);
    try {
      if (!force) {
        const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
        if (cached && Date.now() - cached.fetched_at < TTL_MS) {
          setData(cached);
          return;
        }
      }
      const fresh = await fetchIndicators();
      localStorage.setItem(CACHE_KEY, JSON.stringify(fresh));
      setData(fresh);
    } catch {
      // graceful fallback: keep stale if any
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
      if (cached) setData(cached);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(false);
    // auto-refresh once an hour while the tab is open
    const t = setInterval(() => load(true), TTL_MS);
    return () => clearInterval(t);
  }, []);

  const isDark = variant === "dark";
  const wrap = isDark
    ? "bg-slate-900/60 border-slate-700 text-slate-100"
    : "bg-white border-zinc-200 text-slate-700";
  const labelCls = isDark ? "text-slate-300" : "text-slate-600";

  // Chilean flag (compact SVG): white top half + red bottom half + blue canton with white star
  const Flag = () => (
    <svg viewBox="0 0 30 20" className="w-5 h-[14px] rounded-[2px] overflow-hidden shadow-sm ring-1 ring-black/10" aria-label="Bandera de Chile">
      <rect width="30" height="10" fill="#ffffff" />
      <rect y="10" width="30" height="10" fill="#D52B1E" />
      <rect width="10" height="10" fill="#0033A0" />
      <polygon
        fill="#ffffff"
        points="5,2.5 5.92,4.84 8.45,5 6.45,6.55 7.13,9 5,7.6 2.87,9 3.55,6.55 1.55,5 4.08,4.84"
      />
    </svg>
  );

  return (
    <div
      className={`hidden md:flex items-center gap-2.5 px-3 py-1.5 rounded-full border text-xs ${wrap}`}
      title={data?.fecha ? `Indicadores Chile · actualizado ${new Date(data.fecha).toLocaleDateString("es-CL", { timeZone: "America/Santiago" })}` : "Indicadores Chile"}
      data-testid="chile-indicators"
    >
      <Flag />
      <div className="flex items-center gap-1.5">
        <span className={`${labelCls} font-bold tracking-wide`} style={{ color: "#0033A0" }}>UF</span>
        <span className="font-bold tabular-nums" style={{ color: isDark ? "#fff" : "#0033A0" }}>{formatCLP(data?.uf)}</span>
      </div>
      <div className="h-3 w-px bg-current opacity-25" />
      <div className="flex items-center gap-1.5">
        <span className="font-bold tracking-wide" style={{ color: "#D52B1E" }}>USD</span>
        <span className="font-bold tabular-nums" style={{ color: isDark ? "#fff" : "#D52B1E" }}>{formatCLP(data?.dolar)}</span>
      </div>
      <button
        type="button"
        onClick={() => load(true)}
        className={`ml-1 opacity-50 hover:opacity-100 transition ${loading ? "animate-spin" : ""}`}
        title="Actualizar"
        data-testid="chile-indicators-refresh"
      >
        <RefreshCw className="w-3 h-3" />
      </button>
    </div>
  );
}
